import React from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Paragraph,
  TextTitle,
  TextWrapper,
  FlexWrapper,
  LinkWrapper
} from './ForgotPassword.style';
import Routes from 'src/modules/Navigator/Navigator.route';
import PrimaryButton from 'src/components/PrimaryButton/PrimaryButton.component';
import { t } from 'src/i18n';
import API from 'src/api/api';
import { isKeyPressEnterEvent, parseJSON } from 'src/utils/common.util';
import ErrorPopup from 'src/components/ErrorPopup/ErrorPopup';
import ErrorMessageWithIcon from 'src/components/ErrorMessageWithIcon/ErrorMessageWithIcon.component';
import LoadingContainer from 'src/components/LoadingContainer/LoadingContainer';

interface IProps {
  setIsRequestResetSuccessful(isSuccessful: boolean): void;
  setSuccessMessage(message: string): void;
}

const ForgotPasswordForm: React.FC<IProps> = ({
  setIsRequestResetSuccessful,
  setSuccessMessage
}) => {
  const [emailError, setEmailError] = React.useState<string>('');
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [showErrorPopup, setShowErrorPopup] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const {
    handleSubmit,
    values,
    handleChange,
    touched,
    errors,
    handleBlur,
    setFieldValue
  } = useFormik({
    initialValues: {
      email: ''
    },

    onSubmit: async (val) => {
      setIsLoading(true);
      try {
        const accountId = (parseJSON('tenantInfo') as any).accountId;
        const response = await API().post('/auth/v1/public/forgot-password', {
          ...val,
          accountId
        });
        if (response.status !== 200) {
          return;
        }
        setSuccessMessage(response.data.message);
        setIsRequestResetSuccessful(true);
        setIsLoading(false);
      } catch (error) {
        if (error.response.status) {
          setErrorMessage(error.response.data.message);
          setShowErrorPopup(true);
          setIsLoading(false);

          return;
        }
        setErrorMessage(error);
        setShowErrorPopup(true);
        setIsLoading(false);
      }
    },

    validationSchema: yup.object().shape({
      email: yup
        .string()
        .email(t('signIn.error.format.email'))
        .required(t('signIn.error.required.email'))
    })
  });

  const directLocation = (pathName) => {
    return pathName;
  };

  const handleKeydown = (event) => {
    if (isKeyPressEnterEvent(event)) {
      event.preventDefault();
      handleSubmit();
    }

    return false;
  };

  const trimWhenBlur = (fieldname) => (e) => {
    const trimValue = e.target.value.trim();
    setFieldValue(fieldname, trimValue, true);
  };

  React.useEffect(() => {
    if (!!errors.email && !!touched.email) {
      setEmailError(errors.email);
    } else {
      setEmailError('');
    }
  }, [errors, touched]);

  return (
    <LoadingContainer isLoading={isLoading} isOverlay={true}>
      <>
        <TextTitle>{t('signIn.forgotPassword')}</TextTitle>
        <Paragraph>{t('signIn.forgotPassword.description')}</Paragraph>
        <form autoComplete="off">
          <FlexWrapper>
            <TextWrapper
              inputProps={{
                style: { fontSize: 20 },
                onBlur: trimWhenBlur('email')
              }}
              onChange={handleChange}
              onKeyDown={handleKeydown}
              name="email"
              value={values.email}
              type="text"
              label={null}
              autoComplete="off"
              variant="outlined"
              placeholder={t('signIn.placeholder.email')}
              style={{ borderBottom: '1px solid rgba(60,128,209,0.09)' }}
              onBlur={handleBlur}
              error={!!errors.email && !!touched.email}
            />
            {emailError && <ErrorMessageWithIcon message={emailError} />}
          </FlexWrapper>
        </form>

        <ErrorPopup
          showErrorPopup={showErrorPopup}
          setShowErrorPopup={setShowErrorPopup}
          errorMessage={errorMessage}
        />

        <PrimaryButton
          onClick={handleSubmit as any}
          title={t('register.button.request')}
          variant="contained"
          titleClassName="forgot-password"
        />

        <LinkWrapper to={directLocation(Routes.SignIn)}>
          {t('register.successful.buttonBack')}{' '}
        </LinkWrapper>
      </>
    </LoadingContainer>
  );
};

export default ForgotPasswordForm;
